import { ReactNode, useRef, useState } from 'react';
import { PropsWithChildren } from 'react';
import React from 'react';
import { debounce } from 'lodash-es';

import { Box, ResponsiveValue, Text, TextProps } from '@chakra-ui/react';

import { useIsomorphicLayoutEffect } from '~/hooks/use-isomorphic-layout-effect';

export type ClampedTextV2Props = PropsWithChildren<
  {
    noOfLines: ResponsiveValue<number>;
    renderButton: (props: { isClamped: boolean; onToggle: () => void }) => ReactNode;
  } & TextProps
>;

function hasOverflow(element: HTMLElement | null) {
  if (!element) return false;
  return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
}

export const ClampedTextV2 = ({ children, noOfLines, renderButton, ...textProps }: ClampedTextV2Props) => {
  const [requiresClamping, setRequiresClamping] = useState(false);
  const [isClamped, setIsClamped] = useState(true);
  const originalTextRef = useRef<HTMLDivElement>(null);

  useIsomorphicLayoutEffect(() => {
    const originalText = originalTextRef.current;
    if (!originalText) return;

    // Measure before the first paint. Deferring the first measurement to the debounced
    // observer below paints the text without the toggle and then grows the container by a
    // line when the toggle lands, which reads as a layout shift.
    setRequiresClamping(hasOverflow(originalText));

    const handleResize = debounce((entries: ResizeObserverEntry[]) => {
      for (const entry of entries) {
        setRequiresClamping(hasOverflow(entry.target as HTMLElement));
      }
    }, 250);

    const observer = new ResizeObserver(handleResize);
    observer.observe(originalText);
    return () => {
      handleResize.cancel();
      observer.disconnect();
    };
  }, []);

  return (
    <>
      <Box position='relative'>
        {/* Note: This is a hack to get the original text height. It must inherit the same
            typography (fontSize/lineHeight/fontFamily/etc.) as the visible text below, otherwise
            it measures the wrong number of lines and clamps text that actually fits. */}
        <Text
          ref={originalTextRef}
          {...textProps}
          as='div'
          border={0}
          margin='-1px'
          noOfLines={noOfLines}
          opacity={0}
          padding={0}
          pointerEvents='none'
          position='absolute'
          sx={{
            ...textProps.sx,
            clip: 'rect(0, 0, 0, 0)',
          }}
          aria-hidden
        >
          {children}
        </Text>
        {/* Note: remove static positioning (i.e. position='relative') so that the following text renders above the dummy */}
        <Text {...textProps} noOfLines={isClamped ? noOfLines : undefined} position='relative'>
          {children}
        </Text>
      </Box>
      {requiresClamping && renderButton({ isClamped, onToggle: () => setIsClamped((prev) => !prev) })}
    </>
  );
};
